/**
 * @author: chenlv
 * @date: 2025/01/20
 * @desc: 休闲专区-品类画像页面
 */
import React, { useRef, useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { cloneDeep } from 'lodash-es';
import { history } from 'umi';
import IconButton from '@intelligence/v3/components/IconButton';
import ReusePageTemplate from '@intelligence/v3/components/ReusePageTemplate';
import Filter from './components/Filter';
import MetricSegment from './components/MetricSegment';
import ChartsSection from './components/ChartsSection';
import GlobalPerformanceCard from './components/ChartsSection/GlobalPerformanceCard';
import MetricDataGames from './components/MetricDataGames';
import ActionBar from './components/ActionBar';
import GenreTable from './components/GenreTable';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { useQueryDefinition } from '@intelligence/hooks/useQueryDefinition';
import useMarketFilterApi from '@intelligence/hooks/useMarketSelector';
import useMetricsDateRange from '@intelligence/v3/_hooks/useMetricsDateRange';
import useHotTagsOptions from '@intelligence/hooks/useHotTagsOptions';
import { parseUrlFilterParams } from '@intelligence/v3/utils/filterTagNameAnalyser';
import { fireWindowResize } from '@intelligence/v3/utils/bom';
import { getAllCountryCodesByRegions } from '@intelligence/v3/utils/region';
import { generateGenreSelectValue } from '@intelligence/v3/utils/genre';
import { buildDateDisabledCallbackMap } from '@intelligence/v3/utils/date';
import { generateSortMetricKey } from '@intelligence/v3/utils/metric';
import { formatRangeTimePageFunc, formatGenerePageFunc } from '@intelligence/components/Filter/util';
import { getOriginalShortLinkFilter, URL_FILTER_KEY, URL_SHORT_LINK_NAME } from '@intelligence/utils/urlPathCfg';
import { writeShortUrlParams } from '@intelligence/services/common';
import { getDefaultDimensionBySource } from './components/DimensionSelect/utils';
import {
  formatFilterFetchParams,
  getGenreKeywordsFromKeyword,
  getProductModelValueByGenreSource,
  buildGenreFilterParamsByChartClick,
} from './utils';
import {
  buildCasualGenreProfileHotTagFilterValue,
  buildCasualGenreProfileShortLinkSearch,
  normalizeCasualGenreProfileFilterValue,
  resolveCasualGenreProfileInitialFilterValue,
  SHORT_LINK_RESET_FILTER_KEY,
} from './utils/initFilter';
import { jumpToMobileGames } from './utils/jumperUrlBuilder';
import { DEFAULT_METRIC } from './components/MetricSegment/constants';
import { DEFAULT_TABLE_SEGMENT } from './components/TableSegment/constants';
import { dbiPageLogMes } from '@intelligence/utils/buriedPointCfg';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import { DEFAULT_SIZE } from '@home/components/TeaComponents/Table/constants';
import { GENRE_SOURCE_MAP } from '@intelligence/components/GenreSelect/constants';
import { ChartType } from '@intelligence/v3/enums/chart';
import { SortMode, SortType } from '@home/enums/common';
import { Relation } from '@intelligence/components/GenreSelect/enums';
import { MAX_ROWS_LIMIT } from '@intelligence/components/DownloadCommon/DownloadPage/utils';
import { DATASOURCE_METRICS } from './constants';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';

import type { PagingQuery } from 'tea-component';
import type { IGenretorValue } from '@intelligence/v3/utils/genre';
import type { ICommonFilterInstance } from '@intelligence/pages/TopCharts/components/CommonFilter';
import type {
  IGenreModuleInstance,
  IGenreModuleUpdateParams,
  IGenreTableModuleInstance,
  IGenreTableModuleUpdateParams,
  TDimension,
  TMetricType,
  TTableSegment,
} from './types';
import type {
  IGenrePieChartClickParams,
  IGenreBarChartClickParams,
  IGenreHorizontalBarChartClickParams,
} from './components/ChartsSection/types';

import './index.scss';

import ICON_ARROW_UP from '@intelligence/assets/svg/arrow-up.svg';
import ICON_ARROW_DOWN from '@intelligence/assets/svg/arrow-down.svg';

const entityType = 'mobile';
const pageLogKey = 'casualGenreProfile';
const pageDownloadLogKey = 'casualGenreProfileDownload';
const dataSource = 'sensortower';
const CASUAL_GENRE_PROFILE_INITIAL_FILTER_TAG_NAME_KEY = '__CASUAL_GENRE_PROFILE_INITIAL_FILTER_TAG_NAME__';

interface ICasualGenreProfileProps extends IBaseProps { }

const getInitialFilterTagName = (): string => {
  const filterTagName = new URLSearchParams(window.location.search).get('filter_tag_name') || '';
  const globalWindow = window as any;
  if (filterTagName) {
    globalWindow[CASUAL_GENRE_PROFILE_INITIAL_FILTER_TAG_NAME_KEY] = filterTagName;
    return filterTagName;
  }

  return globalWindow[CASUAL_GENRE_PROFILE_INITIAL_FILTER_TAG_NAME_KEY] || '';
};

const granularityDateRangeCalcConfigMap: Record<IGranularity, { unit: any; units: any; offset: number }> = {
  daily: {
    unit: 'day',
    units: 'days',
    offset: 30,
  },
  weekly: {
    unit: 'week',
    units: 'weeks',
    offset: 4,
  },
  monthly: {
    unit: 'month',
    units: 'months',
    offset: 6,
  },
  quarterly: {
    unit: 'quarter',
    units: 'quarters',
    offset: 4,
  },
  yearly: {
    unit: 'year',
    units: 'years',
    offset: 3,
  },
};

const getInitialDateValue = (
  granularity: IGranularity,
  granularityDateRange?: IMetricDateRange,
): [moment.Moment, moment.Moment] => {
  const dateRangeCalcConfig = granularityDateRangeCalcConfigMap[granularity];
  const maxDate = granularityDateRange?.max_date;

  // 计算默认日期范围：从最大日期往前推6个月（共6个月包括结束月）
  let defaultEndDate = moment().subtract(1, dateRangeCalcConfig.units).endOf(dateRangeCalcConfig.unit);
  let defaultStartDate = moment()
    .subtract(dateRangeCalcConfig.offset, dateRangeCalcConfig.units)
    .startOf(dateRangeCalcConfig.unit);

  if (maxDate) {
    const maxDateMoment = moment(maxDate);
    defaultEndDate = maxDateMoment;
    // 从结束月往前推5个月，共6个月
    defaultStartDate = maxDateMoment
      .clone()
      .subtract(dateRangeCalcConfig.offset - 1, dateRangeCalcConfig.units)
      .startOf(dateRangeCalcConfig.unit);
  }

  return [defaultStartDate, defaultEndDate];
};

const CasualGenreProfile: React.FC<ICasualGenreProfileProps> = (props) => {
  const { style, className } = props;

  const { formatMessage, locale } = useIntlExtend();

  /**
   * 获取系统级配置数据
   * countryMap：国家映射，键为国家code、值为国家信息
   * regionOptions: 地区国家列表
   */
  const { loadingStd: filterConditionsLoading, genreOptionsMap, countryMapNew: countryMap } = useQueryDefinition();

  /**
   * 获取地区选项和有数据的日期范围
   */
  const { filterLoading, regionOptions } = useMarketFilterApi({ filterType: 'common' });

  const { loading: metricsGranularityDateRangesLoading, granularityDateRangeMap: metricsGranularityDateRangeMap } =
    useMetricsDateRange(!filterLoading, {
      game_ids: [],
      app_ids: [],
      granularities: ['daily', 'monthly'],
      markets: getAllCountryCodesByRegions(regionOptions),
      metrics: ['sensortower__dau', 'sensortower__mau', 'sensortower__download', 'sensortower__revenue'],
      group_by: ['market'],
    });

  /**
   * 构建日期禁用映射（禁用超出数据范围的日期）
   */
  const rangePickerDisabledMap = useMemo<IDateRangeDisabledCallbackDateTypeMap>(() => {
    if (metricsGranularityDateRangesLoading) {
      return {};
    }
    return buildDateDisabledCallbackMap(metricsGranularityDateRangeMap);
  }, [metricsGranularityDateRangesLoading, metricsGranularityDateRangeMap]);

  /**
   * 计算默认筛选器值（根据有数据的日期范围）
   */
  const defaultFilterValue = useMemo(() => {
    const monthlyDateValue = getInitialDateValue('monthly', metricsGranularityDateRangeMap?.monthly);

    return {
      [filterKeyMap.MY_FILTER]: {
        value: '',
      },
      [filterKeyMap.RANGE_TIME]: {
        type: 'month',
        value: monthlyDateValue,
      },
      [filterKeyMap.GENRETOR_WITH_SEGMENT]: {
        source: GENRE_SOURCE_MAP.APP_MAGIC,
        mainGenres: [],
        subGenres: [],
        level3Genres: [],
        level4Genres: [],
      },
      [filterKeyMap.HOT_TAGS_SOURCE]: {
        value: null,
      },
      [filterKeyMap.REGION]: {
        type: 'market',
        regions: [],
        markets: ['global'],
      },
      [filterKeyMap.PLATFORM]: {
        value: [],
      },
      [filterKeyMap.ALL_COMPANYS]: {
        value: [],
      },
      [filterKeyMap.RELEASE_TIME]: {
        value: ['', ''],
      },
      [filterKeyMap.SENSOR_TOWER_TAG_PRODUCT_MODEL]: {
        value: [],
      },
      [filterKeyMap.APPMAGIC_TAG_THEMES]: {
        value: [],
      },
      [filterKeyMap.APPMAGIC_TAG_FEATURES]: {
        value: [],
      },
      [filterKeyMap.APPMAGIC_TAG_IP]: {
        value: [],
      },
      [filterKeyMap.APPMAGIC_TAG_SETTINGS]: {
        value: [],
      },
      [filterKeyMap.APPMAGIC_TAG_ART_STYLES]: {
        value: [],
      },
    };
  }, [metricsGranularityDateRangeMap]);

  /**
   * 筛选器实例
   */
  const filterInstance = useRef<ICommonFilterInstance | null>(null);

  /**
   * 热门标签选项，url 不传则自动从当前页面获取
   * filterTagName 用于确定 HotTagsSource 的默认选中项
   */
  const initialFilterTagNameRef = useRef(getInitialFilterTagName());
  const initialFilterTagName = initialFilterTagNameRef.current;
  const { hotTagsOptions, hotTagsLoading } = useHotTagsOptions('mobile');

  /** 当前选中的 HotTag option，用于下载文件名前缀 */
  const [selectedHotTagOption, setSelectedHotTagOption] = useState<any>(null);
  /** 页面初始化时匹配到的默认 HotTag option，用于 reset 时恢复 */
  const [hotTagDefaultOption, setHotTagDefaultOption] = useState<any>(null);
  const resetBaseFilterValueRef = useRef<IRecord | null>(null);
  const [resetBaseFilterValue, setResetBaseFilterValue] = useState<IRecord | null>(null);
  const [pageInitialized, setPageInitialized] = useState(false);
  const hasInitializedRef = useRef(false);
  const skipNextShortLinkSyncRef = useRef(false);

  /**
   * hotTagsOptions 加载完成后，找到匹配 filterTagName 的选项，解析并应用默认筛选值
   */
  // 使用 ref 保存最新的 genreOptionsMap，避免 useCallback 闭包陈旧引用
  const genreOptionsMapRef = useRef(genreOptionsMap);
  genreOptionsMapRef.current = genreOptionsMap;

  const handleHotTagConfirm = React.useCallback(async (option: any, isInitialize = false) => {
    if (!option?.value) return;
    setSelectedHotTagOption(option);
    try {
      const parsedFilterValue = await parseUrlFilterParams(option.value, { genreOptionsMap: genreOptionsMapRef.current });
      if (parsedFilterValue && Object.keys(parsedFilterValue).length > 0) {
        const nextFilterValue = isInitialize
          ? normalizeCasualGenreProfileFilterValue(parsedFilterValue, defaultFilterValue, option)
          : buildCasualGenreProfileHotTagFilterValue({
            parsedHotTagFilterValue: parsedFilterValue,
            currentFilterValue: latestFilterValue.current || draftFilterValue,
            defaultFilterValue,
            hotTagOption: option,
          });
        setDraftFilterValue(nextFilterValue);
        if (isInitialize) {
          setFilterValue(nextFilterValue);
          resetBaseFilterValueRef.current = nextFilterValue;
          setResetBaseFilterValue(nextFilterValue);
          skipNextShortLinkSyncRef.current = true;
          filterInstance.current?.setValueAndConfirm(nextFilterValue);
        } else {
          filterInstance.current?.setFormValue(nextFilterValue);
        }
      }
    } catch (error) {
      console.error('[CasualGenreProfile] handleHotTagConfirm failed:', error);
    }
  }, [defaultFilterValue, draftFilterValue]);

  const syncShortLinkFilterValue = React.useCallback(async (confirmedFilterValue: IRecord) => {
    try {
      const res = await writeShortUrlParams({
        system_type: 'intelligence',
        params: JSON.stringify({
          [URL_FILTER_KEY]: confirmedFilterValue || {},
          [SHORT_LINK_RESET_FILTER_KEY]: resetBaseFilterValueRef.current || defaultFilterValue,
        }),
      });
      const shortLinkId = res?.data?.key || '';
      if (!shortLinkId) {
        return;
      }
      history.replace(buildCasualGenreProfileShortLinkSearch(history.location.search || '', shortLinkId));
    } catch (error) {
      console.error('[CasualGenreProfile] syncShortLinkFilterValue failed:', error);
    }
  }, [defaultFilterValue]);

  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }
    if (filterConditionsLoading || filterLoading || metricsGranularityDateRangesLoading) {
      return;
    }

    const hasShortLink = !!history.location.query?.[URL_SHORT_LINK_NAME];
    if (initialFilterTagName && hotTagsLoading) {
      return;
    }

    let cancelled = false;
    const initialize = async () => {
      let shortLinkFilterValue: IRecord | null = null;
      let shortLinkResetFilterValue: IRecord | null = null;
      let parsedHotTagFilterValue: IRecord | null = null;

      try {
        if (hasShortLink) {
          const shortLinkContext = await getOriginalShortLinkFilter();
          shortLinkFilterValue = shortLinkContext?.[URL_FILTER_KEY] || null;
          shortLinkResetFilterValue = shortLinkContext?.[SHORT_LINK_RESET_FILTER_KEY] || null;
        } else if (initialFilterTagName) {
          const matchedOption = hotTagsOptions.find((option) => option?.name === initialFilterTagName);
          if (matchedOption?.value) {
            parsedHotTagFilterValue = await parseUrlFilterParams(matchedOption.value, {
              genreOptionsMap: genreOptionsMapRef.current,
            });
          }
        }
      } catch (error) {
        console.error('[CasualGenreProfile] initialize filter failed:', error);
      }

      if (cancelled) {
        return;
      }

      const {
        filterValue: initialFilterValue,
        resetFilterValue: initialResetFilterValue,
        hotTagOption,
      } = resolveCasualGenreProfileInitialFilterValue({
        defaultFilterValue,
        shortLinkFilterValue,
        shortLinkResetFilterValue,
        filterTagName: initialFilterTagName,
        hotTagsOptions,
        parsedHotTagFilterValue,
      });
      const initialHotTagOption = initialFilterTagName
        ? hotTagsOptions.find((option) => option?.name === initialFilterTagName) || null
        : null;
      const resetFilterValueWithInitialHotTag = {
        ...initialResetFilterValue,
        [filterKeyMap.HOT_TAGS_SOURCE]: {
          value: initialHotTagOption,
        },
      };

      setSelectedHotTagOption(hotTagOption);
      setHotTagDefaultOption(initialHotTagOption);
      setFilterValue(initialFilterValue);
      setDraftFilterValue(initialFilterValue);
      resetBaseFilterValueRef.current = resetFilterValueWithInitialHotTag;
      setResetBaseFilterValue(resetFilterValueWithInitialHotTag);
      latestFilterValue.current = cloneDeep(initialFilterValue);

      if (filterInstance.current) {
        skipNextShortLinkSyncRef.current = true;
        filterInstance.current.setValueAndConfirm(initialFilterValue);
      } else {
        skipNextShortLinkSyncRef.current = false;
      }
      hasInitializedRef.current = true;
      setPageInitialized(true);
    };

    void initialize();

    return () => {
      cancelled = true;
    };
  }, [
    filterConditionsLoading,
    filterLoading,
    metricsGranularityDateRangesLoading,
    initialFilterTagName,
    hotTagsLoading,
    hotTagsOptions,
    defaultFilterValue,
  ]);
  /**
   * 保存页面初始化的日期value
   */
  const initialDateValueMap = useMemo(() => {
    const dailyDateValue = getInitialDateValue('daily', metricsGranularityDateRangeMap?.daily);
    const monthlyDateValue = getInitialDateValue('monthly', metricsGranularityDateRangeMap?.monthly);

    return { date: dailyDateValue, month: monthlyDateValue };
  }, [metricsGranularityDateRangeMap]);

  /**
   * 保存页面初始化时的品类筛选器值
   * 用于重置时恢复到初始品类选项，而不是硬编码的空数组
   */
  const [initialGenreValue, setInitialGenreValue] = useState<IGenretorValue | null>(null);

  /**
   * 筛选器change后的品类value（还没有点击Confirm）
   */
  const latestGenreValue = useRef<IGenretorValue | null>(null);

  /**
   * 筛选器change后的value（还没有点击Confirm）
   */
  const latestFilterValue = useRef<any>(null);

  /**
   * echarts图表模块实例
   */
  const chartsSectionInstance = useRef<IGenreModuleInstance | null>(null);

  /**
   * 游戏模块实例
   */
  const gamesSectionInstance = useRef<IGenreModuleInstance | null>(null);

  /**
   * 全球变现模块实例
   */
  const globalPerformanceSectionInstance = useRef<IGenreModuleInstance | null>(null);

  /**
   * 表格模块实例
   */
  const tableSectionInstance = useRef<IGenreTableModuleInstance | null>(null);

  /**
   * 筛选器值
   */
  const [filterValue, setFilterValue] = useState<IRecord>(defaultFilterValue);
  const [draftFilterValue, setDraftFilterValue] = useState<IRecord>(defaultFilterValue);

  /**
   * 筛选器参数
   */
  const [filterParams, setFilterParams] = useState<IRecord>({});

  /**
   * 当前选中的指标
   */
  const [metric, setMetric] = useState<TMetricType>(DEFAULT_METRIC);

  /**
   * 维度筛选器状态
   */
  const [dimension, setDimension] = useState<TDimension>(() => {
    return getDefaultDimensionBySource(genreSource);
  });

  /**
   * 表格展示列Segment状态
   */
  const [tableSegment, setTableSegment] = useState<TTableSegment>(DEFAULT_TABLE_SEGMENT);

  /**
   * 从筛选器值中获取品类数据源
   * 注意：筛选器使用 GENRETOR_WITH_SEGMENT 作为 key 存储品类数据
   */
  const genreSource = useMemo<IGenreSource>(() => {
    // 优先从 GENRETOR_WITH_SEGMENT 读取，兼容 GENRETOR
    const genreValue = filterValue?.[filterKeyMap.GENRETOR_WITH_SEGMENT] || filterValue?.[filterKeyMap.GENRETOR];
    return genreValue?.source || GENRE_SOURCE_MAP.SENSORTOWER;
  }, [filterValue]);

  /**
   * 当前排序状态列表
   * 代码支持多字段排序，但实际业务目前只有单字段排序，预防后续业务调整支持多字段排序，所以这里直接用数组类型
   */
  const [sorts, setSorts] = useState<IRatioMetricSortState<ICasualGenreProfileMetricData>[]>([
    { dataSource, metric: 'download', mode: SortMode.Number, type: SortType.Desc },
  ]);

  /**
   * 表格分页状态
   */
  const [pagingQuery, setPagingQuery] = useState<PagingQuery>({
    pageIndex: 1,
    pageSize: DEFAULT_SIZE,
  });

  /**
   * 表格搜索关键字
   */
  const [keyword, setKeyword] = useState<string>('');

  /**
   * 游戏列表展开/折叠状态（默认展开）
   */
  const [gamesListExpanded, setGamesListExpanded] = useState(true);

  const getDownloadReqParams = () => {
    const metricOrderBy = generateSortMetricKey({ dataSource, metric }, SortType.Desc);
    const filterFetchParams = formatFilterFetchParams(filterParams);

    // 计算 genre_keywords
    const genreKeywords = getGenreKeywordsFromKeyword({
      genreSource,
      genreOptionsMap,
      genreDimension: dimension,
      keyword,
    });

    return {
      ...filterFetchParams,
      metrics: DATASOURCE_METRICS,
      group_by: [],
      order_by: [metricOrderBy],
      page: 1,
      page_size: MAX_ROWS_LIMIT,
      genre_keywords: genreKeywords,
      use_custom_fields: [],
      // 传递品类数据源信息给下载弹窗，用于动态更新Genre选项
      genreSource,
      // 传递当前页面选中的品类维度（驼峰命名），用于下载弹窗默认选中
      dimension,
    };
  };

  /**
   * 刷新表格数据（使用当前参数和指定的排序/分页状态）
   */
  const refreshTable = (
    newSorts?: IRatioMetricSortState<ICasualGenreProfileMetricData>[],
    newPagingQuery?: PagingQuery,
    searchKeyword?: string,
  ): void => {
    const finalKeyword = searchKeyword ?? keyword;

    // 计算 genre_keywords
    const genreKeywords = getGenreKeywordsFromKeyword({
      genreSource,
      genreOptionsMap,
      genreDimension: dimension,
      keyword: finalKeyword,
    });

    const updateParams: IGenreTableModuleUpdateParams = {
      genreSource,
      genreDimension: dimension,
      dataSource,
      metric,
      filterParams,
      sorts: newSorts ?? sorts,
      pagingQuery: newPagingQuery ?? pagingQuery,
      genreKeywords,
    };

    tableSectionInstance.current?.update(updateParams);
  };

  const handleFilterChange = (key: string, value1: any, value2: any) => {
    latestGenreValue.current = value1;
    latestFilterValue.current = value2;
    if (value2) {
      setDraftFilterValue(value2);
    }
  };

  /**
   * 处理品类数据源切换，联动更新 Product Model 筛选器
   * @param key 筛选器 key
   * @param value 新值
   */
  const adjustFormValue = (key: string, value: any) => {
    // 只处理品类数据源筛选器
    if (key !== filterKeyMap.GENRETOR_WITH_SEGMENT) {
      return {};
    }

    // 获取新的品类数据源（兼容 value 和 newValue 两种格式）
    const newGenreSource = value?.[filterKeyMap.GENRETOR_WITH_SEGMENT]?.source;

    if (!newGenreSource) {
      return {};
    }

    // 仅在切换品类筛选器数据源类型时，才执行这部分处理逻辑
    if (newGenreSource !== latestFilterValue.current?.[filterKeyMap.GENRETOR_WITH_SEGMENT]?.source) {
      // 当切换到 appmagic 时，联动勾选 Casual 和 Hypercasual 一级品类
      if (newGenreSource === GENRE_SOURCE_MAP.APP_MAGIC) {
        const genreValue = generateGenreSelectValue({
          optionsMap: genreOptionsMap,
          source: GENRE_SOURCE_MAP.APP_MAGIC,
          sourceValue: [
            { value: 'Casual', relation: Relation.Or },
            { value: 'Hypercasual', relation: Relation.Or },
          ],
        });

        return {
          [filterKeyMap.GENRETOR_WITH_SEGMENT]: {
            ...genreValue,
            options: genreOptionsMap?.appmagic || [], // 添加 options 字段，用于全选判断
          },
          [filterKeyMap.SENSOR_TOWER_TAG_PRODUCT_MODEL]: {
            value: [],
          },
        };
      }

      // sensortower 或其他数据源：逻辑保持不变
      // 获取对应数据源的 Product Model 值
      const productModelValues = getProductModelValueByGenreSource(newGenreSource);

      return {
        [filterKeyMap.SENSOR_TOWER_TAG_PRODUCT_MODEL]: {
          value: productModelValues,
        },
      };
    }

    // 返回空对象兜底，防止调用者代码报错
    return {};
  };

  /**
   * 处理搜索关键字变更
   * @param value 搜索关键字
   */
  const handleKeywordChange = (value: string): void => {
    setKeyword(value);
    // 搜索时重置页码为第一页
    const newPagingQuery: PagingQuery = { ...pagingQuery, pageIndex: 1 };
    setPagingQuery(newPagingQuery);
    refreshTable(sorts, newPagingQuery, value);
  };

  /**
   * 处理排序
   * @param sorts 排序状态列表
   */
  const handleSortsChange = (sorts: IRatioMetricSortState<ICasualGenreProfileMetricData>[]): void => {
    const pagingQueryNew = { ...pagingQuery, pageIndex: 1 };
    setSorts(sorts);
    setPagingQuery(pagingQueryNew);
    refreshTable(sorts, pagingQueryNew);
  };

  /**
   * 处理分页
   * @param query 分页参数
   */
  const handlePagingChange = (query: PagingQuery): void => {
    setPagingQuery(query);
    refreshTable(sorts, query);
  };

  /**
   * 处理指标切换
   */
  const handleMetricChange = (metric: TMetricType): void => {
    const updateParams: IGenreModuleUpdateParams = {
      genreSource,
      dataSource,
      metric,
      filterParams,
    };

    setMetric(metric);

    chartsSectionInstance.current?.update(updateParams);
    gamesSectionInstance.current?.update(updateParams);
    globalPerformanceSectionInstance.current?.update(updateParams);
    tableSectionInstance.current?.update({
      ...updateParams,
      genreDimension: dimension,
      sorts,
      pagingQuery,
    });
  };

  /**
   * 根据日期粒度获取纠错后的指标
   * @param currentMetric 当前指标
   * @param granularity 日期粒度
   * @returns 纠错后的指标
   */
  const getAdjustedMetric = (currentMetric: TMetricType, granularity?: IGranularity): TMetricType => {
    if (!['dau', 'wau', 'mau'].includes(currentMetric)) {
      return currentMetric;
    }
    if (granularity === 'daily') {
      return 'dau';
    }
    if (granularity === 'monthly') {
      return 'mau';
    }
    return currentMetric;
  };

  /**
   * 处理筛选器确认
   */
  const handleFilterConfirm = async (value: IRecord, filter: IRecord): Promise<void> => {
    const genreFilterValue = filter?.[filterKeyMap.GENRETOR_WITH_SEGMENT] || filter?.[filterKeyMap.GENRETOR];
    const convertedGenreParams = formatGenerePageFunc(
      {
        ...genreFilterValue,
        options: genreOptionsMap?.[genreFilterValue?.source] || [],
      },
      '',
      { isStdGenreValuesHandler: true },
    );
    const filterParamsNew = {
      ...value,
      ...convertedGenreParams,
    };
    const genreSource = filterParamsNew?.genre_source || genreFilterValue?.source || GENRE_SOURCE_MAP.SENSORTOWER;
    // 根据日期粒度获取纠错后的指标
    const adjustedMetric = getAdjustedMetric(metric, filterParamsNew?.granularity);
    const updateParams: IGenreModuleUpdateParams = {
      genreSource,
      dataSource,
      metric: adjustedMetric,
      filterParams: filterParamsNew,
    };
    // 品类数据源变化时，重置维度筛选器为该数据源的默认值
    const defaultDimension = getDefaultDimensionBySource(genreSource);
    // 根据日期粒度获取纠错后的排序指标
    const adjustedSortMetric = getAdjustedMetric(sorts?.[0]?.metric as TMetricType, filterParamsNew?.granularity);
    const newSorts: IRatioMetricSortState<ICasualGenreProfileMetricData>[] = [
      { dataSource, metric: adjustedSortMetric, mode: SortMode.Number, type: SortType.Desc },
    ];
    const newPagingQuery: PagingQuery = { ...pagingQuery, pageIndex: 1 };

    // 记录初始品类值（仅第一次确认时记录）
    // 这样在重置时可以恢复到页面初始化时的品类选项，而不是硬编码的空数组
    if (initialGenreValue === null) {
      const genreValue = filter?.[filterKeyMap.GENRETOR_WITH_SEGMENT] || filter?.[filterKeyMap.GENRETOR];
      if (genreValue?.source) {
        setInitialGenreValue({
          source: genreValue.source,
          mainGenres: genreValue.mainGenres || [],
          subGenres: genreValue.subGenres || [],
          // App Magic 品类使用 level3Genres 和 level4Genres
          level3Genres: genreValue.level3Genres || [],
          level4Genres: genreValue.level4Genres || [],
        });
      }
    }
    setFilterValue(filter);
    setDraftFilterValue(filter);
    setFilterParams(filterParamsNew);
    latestGenreValue.current = cloneDeep(genreFilterValue);
    latestFilterValue.current = cloneDeep(filter);
    setMetric(adjustedMetric);
    setDimension(defaultDimension);
    setSorts(newSorts);
    setPagingQuery(newPagingQuery);
    setSelectedHotTagOption(filter?.[filterKeyMap.HOT_TAGS_SOURCE]?.value || null);

    if (skipNextShortLinkSyncRef.current) {
      skipNextShortLinkSyncRef.current = false;
    } else {
      void syncShortLinkFilterValue(filter);
    }

    chartsSectionInstance.current?.update(updateParams);
    gamesSectionInstance.current?.update(updateParams);
    globalPerformanceSectionInstance.current?.update(updateParams);
    tableSectionInstance.current?.update({
      ...updateParams,
      genreDimension: defaultDimension,
      sorts: newSorts,
      pagingQuery: newPagingQuery,
      genreKeywords: getGenreKeywordsFromKeyword({
        genreSource,
        genreDimension: defaultDimension,
        keyword,
        genreOptionsMap,
      }),
    });
  };

  /**
   * 处理筛选器重置
   */
  const handleFilterReset = async (value?: IRecord): Promise<void> => {
    let resetBaseFilterValue = value || resetBaseFilterValueRef.current || defaultFilterValue;
    if (hotTagDefaultOption?.value) {
      try {
        const parsedHotTagFilterValue = await parseUrlFilterParams(hotTagDefaultOption.value, {
          genreOptionsMap: genreOptionsMapRef.current,
        });
        if (parsedHotTagFilterValue && Object.keys(parsedHotTagFilterValue).length > 0) {
          resetBaseFilterValue = buildCasualGenreProfileHotTagFilterValue({
            parsedHotTagFilterValue,
            currentFilterValue: resetBaseFilterValue,
            defaultFilterValue: resetBaseFilterValue,
            hotTagOption: hotTagDefaultOption,
          });
        }
      } catch (error) {
        console.error('[CasualGenreProfile] reset hot tag filter failed:', error);
      }
    }
    const resetGenreValue =
      resetBaseFilterValue?.[filterKeyMap.GENRETOR_WITH_SEGMENT] ||
      resetBaseFilterValue?.[filterKeyMap.GENRETOR] ||
      defaultFilterValue?.[filterKeyMap.GENRETOR_WITH_SEGMENT];
    const resetGenreSource = resetGenreValue?.source || GENRE_SOURCE_MAP.APP_MAGIC;

    // 获取对应数据源的 Product Model 值
    const productModelValues = getProductModelValueByGenreSource(resetGenreSource);

    // Reset 回到首次进入页面时的筛选基线；从详情页返回时该基线来自短链接保存的 reset_filter。
    const resetFilterValue = {
      ...resetBaseFilterValue,
      [filterKeyMap.GENRETOR_WITH_SEGMENT]: {
        source: resetGenreSource,
        mainGenres: resetGenreValue?.mainGenres || [],
        subGenres: resetGenreValue?.subGenres || [],
        // App Magic 品类使用 level3Genres 和 level4Genres
        level3Genres: resetGenreValue?.level3Genres || [],
        level4Genres: resetGenreValue?.level4Genres || [],
      },
      [filterKeyMap.SENSOR_TOWER_TAG_PRODUCT_MODEL]: {
        value: resetBaseFilterValue?.[filterKeyMap.SENSOR_TOWER_TAG_PRODUCT_MODEL]?.value || productModelValues,
      },
      [filterKeyMap.HOT_TAGS_SOURCE]: {
        value: hotTagDefaultOption,
      },
    };

    const rangeTimeFilterValue = resetFilterValue?.[filterKeyMap.RANGE_TIME];
    const convertedRangeTimeParams = formatRangeTimePageFunc(rangeTimeFilterValue);
    // 转换品类参数为 API 格式
    const genreFilterValue = resetFilterValue?.[filterKeyMap.GENRETOR_WITH_SEGMENT];
    const convertedGenreParams = formatGenerePageFunc(
      {
        ...genreFilterValue,
        options: genreOptionsMap?.[genreFilterValue?.source] || [],
      },
      '',
      { isStdGenreValuesHandler: true },
    );

    // 将转换后的品类参数合并到 resetFilterValue
    const resetFilterValueWithApiParams = {
      ...resetFilterValue,
      ...convertedRangeTimeParams,
      ...convertedGenreParams,
    };

    // 品类数据源变化时，重置维度筛选器为该数据源的默认值
    const defaultDimension = getDefaultDimensionBySource(resetGenreSource);
    const newSorts: IRatioMetricSortState<ICasualGenreProfileMetricData>[] = [
      { dataSource, metric: DEFAULT_METRIC, mode: SortMode.Number, type: SortType.Desc },
    ];
    const newPagingQuery: PagingQuery = { ...pagingQuery, pageIndex: 1, pageSize: DEFAULT_SIZE };
    const updateParams: IGenreModuleUpdateParams = {
      genreSource: resetGenreSource,
      dataSource,
      metric: DEFAULT_METRIC,
      filterParams: resetFilterValueWithApiParams,
    };

    setFilterValue(resetFilterValue);
    setDraftFilterValue(resetFilterValue);
    setFilterParams(resetFilterValueWithApiParams);
    setMetric(DEFAULT_METRIC);
    setDimension(defaultDimension);
    // 重置表格相关状态
    setTableSegment(DEFAULT_TABLE_SEGMENT);
    setSorts(newSorts);
    setPagingQuery(newPagingQuery);
    setSelectedHotTagOption(hotTagDefaultOption || null);

    latestGenreValue.current = cloneDeep(resetGenreValue);
    latestFilterValue.current = cloneDeep(resetFilterValue);
    void syncShortLinkFilterValue(resetFilterValue);

    chartsSectionInstance.current?.update(updateParams);
    gamesSectionInstance.current?.update(updateParams);
    globalPerformanceSectionInstance.current?.update(updateParams);
    tableSectionInstance.current?.update({
      ...updateParams,
      genreDimension: defaultDimension,
      sorts: newSorts,
      pagingQuery: newPagingQuery,
      genreKeywords: getGenreKeywordsFromKeyword({
        genreSource: resetGenreSource,
        genreDimension: defaultDimension,
        keyword,
        genreOptionsMap,
      }),
    });

    filterInstance.current?.setFormValue(resetFilterValue);
  };

  /**
   * 处理维度筛选器变更
   */
  const handleDimensionChange = (value: TDimension): void => {
    setDimension(value);

    // 维度变化时重新计算 genre_keywords
    const genreKeywords = getGenreKeywordsFromKeyword({
      genreSource,
      genreOptionsMap,
      genreDimension: value,
      keyword,
    });

    const updateParams: IGenreTableModuleUpdateParams = {
      genreSource,
      genreDimension: value,
      dataSource,
      metric,
      filterParams,
      sorts,
      pagingQuery,
      genreKeywords,
    };

    tableSectionInstance.current?.update(updateParams);
  };

  /**
   * 处理表格Segment变更
   */
  const handleTableSegmentChange = (value: TTableSegment): void => {
    setTableSegment(value);

    // 根据不同的 Segment 值滚动到对应的列
    let targetColumnKey = '';
    switch (value) {
      case 'metrics':
        targetColumnKey = 'download';
        break;
      case 'topGames':
        targetColumnKey = 'topGamesByDownloads';
        break;
      case 'risingGames':
        targetColumnKey = 'risingGamesByDownloads';
        break;
    }

    if (targetColumnKey) {
      // 延迟执行滚动，确保表格已渲染
      setTimeout(() => {
        tableSectionInstance.current?.scrollToColumn(targetColumnKey);
      }, 100);
    }
  };

  /**
   * 切换游戏列表展开/折叠状态
   */
  const handleToggleGamesList = (): void => {
    setGamesListExpanded((prev) => !prev);
    fireWindowResize();
  };

  /**
   * 处理图表点击事件（饼状图、柱状图、横向柱状图通用）
   * @param chartType 图表类型（用于日志）
   * @param params 点击参数
   */
  const handleGenreChartClick = (
    chartType: ChartType,
    params: IGenrePieChartClickParams | IGenreBarChartClickParams | IGenreHorizontalBarChartClickParams,
  ): void => {
    // console.debug(`[CasualGenreProfile] ${chartType} chart clicked:`, params);

    const genreDimension = params?.genreDimension || 'superGenre';
    const genreName = params?.name || '';

    // others暂时还没确定处理方案的，在跟产品沟通中
    if (genreName === 'others') {
      return;
    }

    // 调用纯函数构建筛选参数
    const { filterValueNew, filterValueNewWithApiParams, updateParams } = buildGenreFilterParamsByChartClick({
      genreOptionsMap,
      genreSource,
      genreDimension,
      genreName,
      dataSource,
      metric,
      filterValue,
      filterParams,
    });

    const newPagingQuery: PagingQuery = { ...pagingQuery, pageIndex: 1, pageSize: DEFAULT_SIZE };

    setFilterValue(filterValueNew);
    setDraftFilterValue(filterValueNew);
    setFilterParams(filterValueNewWithApiParams);
    void syncShortLinkFilterValue(filterValueNew);

    chartsSectionInstance.current?.update(updateParams);
    gamesSectionInstance.current?.update(updateParams);
    globalPerformanceSectionInstance.current?.update(updateParams);
    tableSectionInstance.current?.update({
      ...updateParams,
      genreDimension,
      sorts,
      pagingQuery: newPagingQuery,
      genreKeywords: getGenreKeywordsFromKeyword({
        genreSource: updateParams.genreSource,
        genreDimension,
        keyword,
        genreOptionsMap,
      }),
    });
  };

  /**
   * 处理饼状图扇形点击事件
   * @param params 点击参数
   */
  const handlePieClick = (params: IGenrePieChartClickParams): void => {
    handleGenreChartClick(ChartType.Pie, params);
  };

  /**
   * 处理柱状图柱子点击事件
   * @param params 点击参数
   */
  const handleBarClick = (params: IGenreBarChartClickParams): void => {
    handleGenreChartClick(ChartType.Bar, params);
  };

  /**
   * 处理横向柱状图柱子点击事件
   * @param params 点击参数
   */
  const handleHorizontalBarClick = (params: IGenreHorizontalBarChartClickParams): void => {
    handleGenreChartClick(ChartType.HorizontalBar, params);
  };

  /**
   * 处理表格中More按钮点击事件
   * @param columnKey 列 key
   * @param record 表格行数据
   */
  const handleMoreGamesClick = (columnKey: string, record: ICasualGenreProfileMetricData): void => {
    // 从 filterValue 获取日期范围和粒度
    const rangeTimeValue = filterValue?.[filterKeyMap.RANGE_TIME];
    const granularity = filterParams?.granularity || 'monthly';
    const dateRange = rangeTimeValue?.value || [];

    if (!Array.isArray(dateRange) || dateRange.length !== 2) {
      console.warn('[handleMoreGamesClick] 日期范围无效', dateRange);
      return;
    }

    const [startDate, endDate] = dateRange;

    const genreParams: IGenreInformation & { source: string } = {
      source: genreSource as string,
      sensortower_genre: record?.sensortower_genre || '',
      sensortower_sub_genre: record?.sensortower_sub_genre || '',
      super_genre: record?.super_genre || '',
      major_category: record?.major_category || '',
      mid_category: record?.mid_category || '',
      sub_category: record?.sub_category || '',
    } as any;

    // 调用跳转函数，传递 filterValue 以获取地区筛选器值
    jumpToMobileGames(
      columnKey,
      dataSource,
      granularity,
      [moment(startDate), moment(endDate)],
      genreParams,
      filterValue,
    );
  };

  return (
    <ReusePageTemplate
      style={style}
      className={classnames('dbi-casual-genre-profile affix-target', className)}
      title={formatMessage({ id: 'dbi_casual_genre_profile' })}
      subTitle={formatMessage({ id: 'dbi_casual_genre_profile_sub_title' })}
      pageLogKey={pageLogKey}
    >
      <MarqueeBanner />
      {/* 筛选器 */}
      <Filter
        ref={filterInstance}
        entityType={entityType}
        filterLoading={filterLoading || !pageInitialized}
        regionOptions={regionOptions}
        rangePickerDisabledMap={rangePickerDisabledMap}
        initialDateValueMap={initialDateValueMap}
        initialGenreValue={initialGenreValue}
        filterValue={draftFilterValue}
        adjustFormValue={adjustFormValue}
        onChangeExtraLast={handleFilterChange}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onConfirm={handleFilterConfirm}
        onReset={handleFilterReset}
        hotTagsOptions={hotTagsOptions}
        filterTagName={initialFilterTagName}
        hotTagDefaultOption={hotTagDefaultOption}
        resetBaseFilterValue={resetBaseFilterValue}
        onHotTagConfirm={handleHotTagConfirm}
      />

      {/* 头部区域：指标切换Segment + Noteworthy Games 按钮 */}
      <div className="dbi-casual-genre-profile__header">
        <MetricSegment granularity={filterParams?.granularity} value={metric} onChange={handleMetricChange} />
        <IconButton
          className="dbi-casual-genre-profile__game-list-collapse-btn"
          useIconFont={false}
          suffix={gamesListExpanded ? ICON_ARROW_UP : ICON_ARROW_DOWN}
          onClick={handleToggleGamesList}
        >
          {formatMessage({ id: 'dbi_casual_noteworthy_games' })}
        </IconButton>
      </div>

      {/* 图表与游戏列表左右布局容器 */}
      <div className="dbi-casual-genre-profile__content">
        {/* 品类图表区域 */}
        <ChartsSection
          ref={chartsSectionInstance}
          genreOptionsMap={genreOptionsMap}
          genreSource={genreSource}
          granularity={filterParams?.granularity}
          dataSource={dataSource}
          metric={metric}
          filterParams={filterParams}
          onPieClick={handlePieClick}
          onBarClick={handleBarClick}
          onHorizontalBarClick={handleHorizontalBarClick}
        />

        {/* 游戏列表 */}
        <MetricDataGames
          ref={gamesSectionInstance}
          collapsed={!gamesListExpanded}
          dataSource={dataSource}
          metric={metric}
          filterParams={filterParams}
        />
      </div>

      {/* Global Performance 全球表现卡片（容器下方） */}
      <div className="dbi-casual-genre-profile__global">
        <GlobalPerformanceCard
          ref={globalPerformanceSectionInstance}
          dataSource={dataSource}
          metric={metric}
          countryMap={countryMap}
        />
      </div>

      {/* 操作栏 */}
      <ActionBar
        entityType={entityType}
        downloadLogConfigKey={pageDownloadLogKey}
        genreSource={genreSource}
        getDownloadReqParams={getDownloadReqParams}
        dimension={dimension}
        onDimensionChange={handleDimensionChange}
        tableSegment={tableSegment}
        onTableSegmentChange={handleTableSegmentChange}
        keyword={keyword}
        onKeywordChange={handleKeywordChange}
        downloadFileNamePrefix={selectedHotTagOption
          ? (locale === 'zh-CN'
            ? (selectedHotTagOption.name_cn || selectedHotTagOption.name || '')
            : (selectedHotTagOption.name_en || selectedHotTagOption.name || ''))
          : ''
        }
        pageLogInfo={{
          pageId: dbiPageLogMes.casualGenreProfile.pageId,
          typeId: dbiPageLogMes.casualGenreProfile.typeId,
        }}
      />

      {/* 品类表格 */}
      <GenreTable
        ref={tableSectionInstance}
        entityType={entityType}
        granularity={filterParams?.granularity}
        genreOptionsMap={genreOptionsMap}
        genreSource={genreSource}
        dataSource={dataSource}
        metric={metric}
        dimension={dimension}
        tableSegment={tableSegment}
        sorts={sorts}
        onSortsChange={handleSortsChange}
        pagingQuery={pagingQuery}
        onPagingChange={handlePagingChange}
        onMoreGamesClick={handleMoreGamesClick}
        keyword={keyword}
      />
    </ReusePageTemplate>
  );
};

export default CasualGenreProfile;
